<script lang="ts">
  import { onMount } from 'svelte';
  import { vuAudio } from '$lib/audio/vuAudio.svelte';
  import { playBondSound } from '$lib/audio/sounds';

  /**
   * Full-screen "two souls met" celebration. Fires the synthesized bond
   * sound and renders a themed visual:
   *
   *   - Modern   — glassmorphism card, expanding cyan rings, soft glow
   *   - Brutal   — slab-shadow card, monospace metadata, hard slam-in
   *
   * Theme is detected from `body.brutal` (the existing VuTwin convention
   * — see app.css). A MutationObserver keeps the variant in sync if the
   * user toggles theme while the overlay is open.
   *
   * Reduced-motion: ring/slam animations are stripped, the static fade
   * remains, and the sound still plays — sound IS the celebration for
   * users with motion sensitivity.
   */
  interface Props {
    show: boolean;
    friendName?: string;
    onComplete?: () => void;
  }

  let { show = $bindable(false), friendName = 'twin', onComplete }: Props = $props();

  let theme = $state<'modern' | 'brutal'>('modern');
  let mounted = $state(false);

  function detectTheme() {
    if (typeof document === 'undefined') return;
    theme = document.body.classList.contains('brutal') ? 'brutal' : 'modern';
  }

  onMount(() => {
    detectTheme();
    const observer = new MutationObserver(detectTheme);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    mounted = true;
    return () => observer.disconnect();
  });

  // When `show` flips true, trigger the audio + haptic + auto-dismiss.
  $effect(() => {
    if (show && mounted) {
      void triggerCelebration();
    }
  });

  async function triggerCelebration() {
    // The initiator path is inside a click chain so init() succeeds. The
    // receiver path (driven by the auto-bond inbox poll) will already
    // have an unlocked context thanks to the gesture primer in
    // +page.svelte. If we somehow got here without either, init() will
    // try again — worst case it stays suspended and `getBus()` returns
    // null, making `playBondSound()` a silent no-op.
    if (!vuAudio.ready) await vuAudio.init();
    playBondSound();

    // Subtle haptic on supported mobile browsers — fails silently if
    // unsupported. Skipped under reduced-motion.
    if (
      typeof navigator !== 'undefined' &&
      'vibrate' in navigator &&
      !vuAudio.prefersReducedMotion
    ) {
      try {
        navigator.vibrate([20, 40, 20, 40, 80]);
      } catch {
        /* not all browsers honour the call */
      }
    }

    setTimeout(() => {
      show = false;
      onComplete?.();
    }, 2400);
  }
</script>

{#if show}
  <div
    class="bond-overlay"
    class:modern={theme === 'modern'}
    class:brutal={theme === 'brutal'}
    role="status"
    aria-live="polite"
  >
    <div class="backdrop"></div>

    {#if theme === 'modern'}
      <div class="modern-content">
        <div class="ring ring-1"></div>
        <div class="ring ring-2"></div>
        <div class="ring ring-3"></div>
        <div class="card">
          <svg
            class="check-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            aria-hidden="true"
          >
            <path d="M9 12l2 2 4-4" stroke-linecap="round" stroke-linejoin="round" />
            <circle cx="12" cy="12" r="10" />
          </svg>
          <h2>Bonded</h2>
          <p>
            You're now connected with <strong>@{friendName}</strong>
          </p>
          <div class="key-preview">512-BIT TWIN KEY ESTABLISHED</div>
        </div>
      </div>
    {:else}
      <div class="brutal-content">
        <div class="brutal-card">
          <div class="brutal-header">BOND COMPLETE</div>
          <div class="brutal-body">
            <svg
              class="brutal-check"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="3"
              aria-hidden="true"
            >
              <path d="M5 12l5 5L20 7" stroke-linecap="square" stroke-linejoin="miter" />
            </svg>
            <div class="brutal-name">@{friendName.toUpperCase()}</div>
            <div class="brutal-meta">512-BIT TWIN KEY · ESTABLISHED</div>
          </div>
        </div>
      </div>
    {/if}
  </div>
{/if}

<style>
  .bond-overlay {
    position: fixed;
    inset: 0;
    z-index: 10000;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
  }

  .backdrop {
    position: absolute;
    inset: 0;
    animation: bc-fade 240ms ease both;
  }

  /* ============== MODERN ============== */
  .modern .backdrop {
    background:
      radial-gradient(circle at center, rgba(0, 220, 255, 0.18) 0%, rgba(5, 0, 16, 0.88) 70%);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
  }

  .modern-content {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .ring {
    position: absolute;
    width: 120px;
    height: 120px;
    border: 1px solid var(--border-cool);
    border-radius: 50%;
    animation: bc-ring 1.6s ease-out forwards;
  }
  .ring-1 {
    animation-delay: 0s;
  }
  .ring-2 {
    animation-delay: 0.2s;
  }
  .ring-3 {
    animation-delay: 0.4s;
  }

  .card {
    background: rgba(5, 0, 16, 0.82);
    backdrop-filter: blur(24px);
    -webkit-backdrop-filter: blur(24px);
    border: 1px solid var(--border-cool);
    border-radius: var(--radius-lg);
    padding: clamp(28px, 6vw, 40px) clamp(36px, 8vw, 56px);
    text-align: center;
    color: var(--text);
    box-shadow:
      0 8px 60px rgba(0, 220, 255, 0.4),
      var(--glow-cyan);
    animation: bc-bloom 500ms var(--spring-bouncy) 300ms both;
    max-width: min(92vw, 420px);
  }

  .check-icon {
    width: 56px;
    height: 56px;
    color: var(--cyan);
    margin-bottom: 14px;
    filter: drop-shadow(0 0 12px rgba(0, 220, 255, 0.6));
  }

  .card h2 {
    font-size: clamp(22px, 5vw, 28px);
    font-weight: 800;
    letter-spacing: 0.04em;
    margin: 0 0 6px;
    text-transform: uppercase;
  }

  .card p {
    font-size: 13px;
    color: var(--text-2, rgba(255, 255, 255, 0.7));
    margin: 0 0 14px;
  }

  .card strong {
    color: var(--cyan);
    font-weight: 700;
  }

  .key-preview {
    font-family: ui-monospace, 'SF Mono', 'JetBrains Mono', 'Courier New', monospace;
    font-size: 9px;
    letter-spacing: 0.18em;
    color: rgba(0, 220, 255, 0.7);
    padding-top: 10px;
    border-top: 1px solid var(--border);
  }

  /* ============== BRUTAL ============== */
  .brutal .backdrop {
    background: rgba(255, 255, 255, 0.95);
  }

  .brutal-content {
    position: relative;
    animation: bc-slam 150ms ease-out forwards;
  }

  .brutal-card {
    background: var(--bg);
    border: 4px solid var(--text);
    box-shadow: 8px 8px 0 0 var(--text);
    min-width: min(86vw, 320px);
    max-width: min(92vw, 380px);
  }

  .brutal-header {
    background: var(--text);
    color: var(--bg);
    padding: 12px 24px;
    font-size: 12px;
    font-weight: 900;
    letter-spacing: 0.18em;
    text-align: center;
  }

  .brutal-body {
    padding: 28px 22px;
    text-align: center;
    color: var(--text);
  }

  .brutal-check {
    width: 56px;
    height: 56px;
    color: var(--text);
    margin-bottom: 14px;
  }

  .brutal-name {
    font-size: clamp(20px, 5vw, 24px);
    font-weight: 900;
    letter-spacing: 0.06em;
    color: var(--text);
    margin-bottom: 12px;
    word-break: break-all;
  }

  .brutal-meta {
    font-family: ui-monospace, 'SF Mono', 'JetBrains Mono', 'Courier New', monospace;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.14em;
    color: var(--text);
    padding-top: 12px;
    border-top: 2px solid var(--text);
  }

  /* ============== ANIMATIONS ============== */
  @keyframes bc-fade {
    from {
      opacity: 0;
    }
  }

  @keyframes bc-ring {
    0% {
      transform: scale(0.5);
      opacity: 0;
      border-width: 2px;
    }
    20% {
      opacity: 0.85;
    }
    100% {
      transform: scale(4);
      opacity: 0;
      border-width: 1px;
    }
  }

  @keyframes bc-bloom {
    0% {
      transform: scale(0.7);
      opacity: 0;
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }

  @keyframes bc-slam {
    0% {
      transform: translate(-12px, -12px);
    }
    100% {
      transform: translate(0, 0);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .ring {
      animation: none;
      opacity: 0;
    }
    .card,
    .brutal-content {
      animation: bc-fade 200ms ease both;
    }
  }
</style>
